import { useState, useEffect, useRef } from "react";
import CalendarComponent from "../../../components/Booking/Calendar";

const DateAndTimeScreen = ({
  deliveryDate,
  exactTime,
  handleUpdateBookingObject,
  deliverySlotsInfo,
}) => {
  const [selectedDate, setSelectedDate] = useState(
    deliveryDate ? new Date(deliveryDate) : null
  );
  // Base date for visible date boxes. Defaults to persisted deliveryDate or today.
  const [visibleStartDate, setVisibleStartDate] = useState(() => {
    const base = deliveryDate ? new Date(deliveryDate) : new Date();
    base.setHours(0, 0, 0, 0);
    return base;
  });
  // Ref to detect when a visible card click caused a deliveryDate update
  const lastClickedVisibleRef = useRef(false);
  const [selectedTime, setSelectedTime] = useState(exactTime || "");
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Generate next few dates (4 boxes) starting from visibleStartDate
  const getNextDates = () => {
    const dates = [];
    const start = visibleStartDate ? new Date(visibleStartDate) : new Date();
    start.setHours(0, 0, 0, 0);

    for (let i = 0; i < 4; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Check if date is valid (not past date)
  const isDateValid = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  // Check if date is in recent range (today, tomorrow, day after tomorrow)
  const isDateInRecentRange = (date) => {
    if (!date) return false;
    const today = new Date();
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(today.getDate() + 2);

    return date >= today && date <= dayAfterTomorrow;
  };

  // Check if date is beyond 1 year
  const isDateBeyondOneYear = (date) => {
    const today = new Date();
    const oneYearFromNow = new Date(today);
    oneYearFromNow.setFullYear(today.getFullYear() + 1);
    return date > oneYearFromNow;
  };

  // Check if a time slot is in the past for today
  const isTimeSlotInPast = (timeSlot) => {
    if (
      !selectedDate ||
      selectedDate.toDateString() !== new Date().toDateString()
    ) {
      return false; // Only filter for today's date
    }

    if (timeSlot === "Within 2 hours") {
      return false; // ASAP is always available if shown
    }

    // Extract start time from slot (format: "8AM-9AM" or "8:00 AM-9:00 AM")
    const timeMatch = timeSlot.match(/^(\d{1,2})(:\d{2})?\s*(AM|PM)/i);
    if (!timeMatch) {
      return false; // If we can't parse the time, don't filter it
    }

    const hour = parseInt(timeMatch[1]);
    const minutes = timeMatch[2] ? parseInt(timeMatch[2].substring(1)) : 0;
    const isPM = timeMatch[3].toUpperCase() === "PM";

    // Convert to 24-hour format
    let slotHour = hour;
    if (isPM && hour !== 12) {
      slotHour += 12;
    } else if (!isPM && hour === 12) {
      slotHour = 0;
    }

    // Create a date object for the slot time today
    const slotTime = new Date();
    slotTime.setHours(slotHour, minutes, 0, 0);

    // Compare with current time
    const now = new Date();
    return slotTime <= now;
  };

  // Validate selected date and clear if invalid
  useEffect(() => {
    if (selectedDate) {
      if (!isDateValid(selectedDate) || isDateBeyondOneYear(selectedDate)) {
        setSelectedDate(null);
        setSelectedTime("");
        handleUpdateBookingObject("deliveryDate", "", "10");
        handleUpdateBookingObject("exactTime", "", "11");
      }
    }
  }, [selectedDate, handleUpdateBookingObject]);

  // On mount: default to today if there's no selected date, and ensure visibleStartDate matches
  useEffect(() => {
    if (!selectedDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setSelectedDate(today);
      setVisibleStartDate(today);
      // Immediately update VBO/state as well
      handleUpdateBookingObject(
        "deliveryDate",
        today.toString()?.split(" GMT")[0],
        "10"
      );
    }
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync incoming props into local state so selections persist after refresh
  useEffect(() => {
    if (deliveryDate) {
      const parsed = new Date(deliveryDate);
      if (!selectedDate || parsed.toDateString() !== selectedDate.toDateString()) {
        setSelectedDate(parsed);
      }
      // If this deliveryDate update was caused by a visible-card click,
      // ignore changing visibleStartDate (we only want to shift when
      // restoring from storage or when calendar selection is outside range).
      if (lastClickedVisibleRef.current) {
        lastClickedVisibleRef.current = false;
        return;
      }

      // Only shift the visible start when:
      // - we're restoring from an empty state (no selectedDate yet), OR
      // - the persisted/restored date is outside the current visible 4-date range.
      const start = visibleStartDate ? new Date(visibleStartDate) : new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 3);

      const parsedDay = new Date(parsed);
      parsedDay.setHours(0, 0, 0, 0);

      if (!selectedDate || parsedDay < start || parsedDay > end) {
        setVisibleStartDate(parsedDay);
        setCurrentMonth(new Date(parsedDay.getFullYear(), parsedDay.getMonth(), 1));
      }
    } else if (selectedDate) {
      setSelectedDate(null);
      // Reset visible start to today when persisted date is removed
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setVisibleStartDate(today);
    }
  }, [deliveryDate]);

  useEffect(() => {
    if (typeof exactTime !== "undefined" && exactTime !== selectedTime) {
      setSelectedTime(exactTime || "");
    }
  }, [exactTime]);

  // Auto-show calendar if selected date is beyond recent range (19+)
  useEffect(() => {
    if (selectedDate && !isDateInRecentRange(selectedDate)) {
      // Only show calendar for dates that are 3+ days from today
      const today = new Date();
      const threeDaysFromToday = new Date(today);
      threeDaysFromToday.setDate(today.getDate() + 3);

      if (selectedDate > threeDaysFromToday) {
        // Do NOT auto-open the calendar. Only set the calendar month so
        // when the user opens it manually it will show the selected date's month.
        setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      }
    }
    // Note: We don't auto-hide calendar for recent dates anymore
    // Calendar only closes when user clicks the close button
  }, []);

  // Extract time slots from deliverySlotsInfo API data
  useEffect(() => {
    if (deliverySlotsInfo && Array.isArray(deliverySlotsInfo)) {
      const timeSlots = [];

      // Check if ASAP option is available from API
      const hasASAP = deliverySlotsInfo.some(
        (slotInfo) => slotInfo.isASAP === true
      );

      // Only show "Within 2 hours" if today is selected and ASAP is available
      const isTodaySelected =
        selectedDate &&
        selectedDate.toDateString() === new Date().toDateString();
      if (hasASAP && isTodaySelected) {
        timeSlots.push("Within 2 hours");
      }

      // Extract time slots from API data and format as startTime-endTime
      const formattedSlots = [];
      deliverySlotsInfo.forEach((slotInfo) => {
        if (slotInfo.startTime && slotInfo.endTime) {
          const formattedSlot = `${slotInfo.startTime}-${slotInfo.endTime}`;
          if (!formattedSlots.includes(formattedSlot)) {
            formattedSlots.push({
              display: formattedSlot,
              startTime: slotInfo.startTime,
              isASAP: slotInfo.isASAP,
            });
          }
        }
      });

      // Sort slots by start time (excluding ASAP which stays at top)
      const sortedSlots = formattedSlots
        .filter((slot) => !slot.isASAP) // Remove ASAP from sorting
        .sort((a, b) => {
          // Convert time strings to comparable values for sorting
          const timeA = new Date(`2000-01-01 ${a.startTime}`);
          const timeB = new Date(`2000-01-01 ${b.startTime}`);
          return timeA - timeB;
        })
        .map((slot) => slot.display);

      // Combine ASAP (if available) with sorted slots
      const finalSlots = [];
      if (hasASAP && isTodaySelected) {
        finalSlots.push("Within 2 hours");
      }
      finalSlots.push(...sortedSlots);

      // Filter out past time slots if today is selected
      const filteredSlots = finalSlots.filter(
        (slot) => !isTimeSlotInPast(slot)
      );

      setAvailableTimeSlots(filteredSlots);
    } else {
      // Fallback to default time slots if API data is not available
      const isTodaySelected =
        selectedDate &&
        selectedDate.toDateString() === new Date().toDateString();
      const defaultSlots = [
        "8AM-9AM",
        "9AM-10AM",
        "10AM-11AM",
        "11AM-12PM",
        "12PM-1PM",
        "1PM-2PM",
        "2PM-3PM",
        "5PM-6PM",
        "7PM-8PM",
      ];

      if (isTodaySelected) {
        defaultSlots.unshift("Within 2 hours");
      }

      // Filter out past time slots if today is selected
      const filteredDefaultSlots = defaultSlots.filter(
        (slot) => !isTimeSlotInPast(slot)
      );

      setAvailableTimeSlots(filteredDefaultSlots);
    }
  }, [deliverySlotsInfo, selectedDate]);

  const nextDates = getNextDates();

  const handleDateSelect = (date) => {
    // mark that this update originated from clicking one of the visible boxes
    lastClickedVisibleRef.current = true;
    setSelectedDate(date);
    // Reset exactTime whenever a date is selected
    setSelectedTime("");
    handleUpdateBookingObject("exactTime", "", "11");
    // Ensure calendar will show this date's month when opened next
    const d = new Date(date);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    handleUpdateBookingObject(
      "deliveryDate",
      date.toString()?.split(" GMT")[0],
      "10"
    );

    // Check if user had ASAP selected and is changing from current date
    const isCurrentDate = new Date().toDateString() === date.toDateString();
    const wasASAPSelected = selectedTime === "Within 2 hours";

    if (wasASAPSelected && !isCurrentDate) {
      // Clear the delivery slot and time when ASAP user changes from current date
      // ...already handled above...
      // You might also need to clear deliverySlotId here if you have access to it
      // handleUpdateBookingObject("deliverySlotId", "", "slot_clear");
    }

    // Explicitly ensure calendar is hidden for today and recent dates
    // Always close the calendar after user selects a date from the cards
    setShowCalendar(false);

    // Note: Calendar state is not changed here for extended dates
    // Calendar only opens/closes via "More Dates" button and close button
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    handleUpdateBookingObject("exactTime", time, "11");
  };


  const formatDate = (date) => {
    if (date.toDateString() === new Date().toDateString()) {
      return "Today";
    }
    return date.toLocaleDateString("en-US", { weekday: "short" });
  };

  const formatDay = (date) => {
    return date.getDate();
  };

  // Format a time part (e.g. "07:00 PM" | "8AM" | "9:30 AM" | "08:00")
  const formatTimePart = (part) => {
    if (!part) return part;
    const raw = part.trim();

    // Try to match hour(:minutes)? and optional AM/PM
    const m = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!m) {
      // fallback: return raw lowercased
      return raw.toLowerCase();
    }

    let hour = parseInt(m[1], 10);
    const minutes = m[2] ? parseInt(m[2], 10) : 0;
    const ampm = m[3] ? m[3].toUpperCase() : null;

    // If am/pm not present but hour looks like 24h (>=0 && <=23), infer am/pm
    let isPM = false;
    if (ampm) {
      isPM = ampm === "PM";
    } else {
      if (hour >= 0 && hour <= 23) {
        isPM = hour >= 12;
      }
    }

    // Convert to 12-hour display hour
    let displayHour = hour % 12;
    if (displayHour === 0) displayHour = 12;

    // Format minutes only when not zero
    const minuteStr =
      minutes && minutes !== 0 ? `:${String(minutes).padStart(2, "0")}` : "";

    const suffix = isPM ? "pm" : "am";
    return `${displayHour}${minuteStr}${suffix}`;
  };

  // Format a full slot like "07:00 PM-08:00 PM" or "8AM-9AM" to "9am – 10am"
  const formatSlotDisplay = (slot) => {
    if (!slot) return slot;
    if (slot === "Within 2 hours") return slot;
    const parts = slot.split("-");
    if (parts.length === 1) return formatTimePart(parts[0]);
    const start = formatTimePart(parts[0]);
    const end = formatTimePart(parts[1]);
    return `${start} – ${end}`;
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const oneYearFromNow = new Date(today);
    oneYearFromNow.setFullYear(today.getFullYear() + 1);
    oneYearFromNow.setHours(23, 59, 59, 999);

    return date < today || date > oneYearFromNow;
  };

  const isDateSelected = (date) => {
    return selectedDate && selectedDate.toDateString() === date.toDateString();
  };

  const handleDateClick = (date) => {
    if (!isDateDisabled(date)) {
      setSelectedDate(date);
      // Reset exactTime whenever a date is selected
      setSelectedTime("");
      handleUpdateBookingObject("exactTime", "", "11");
      handleUpdateBookingObject(
        "deliveryDate",
        date.toString()?.split(" GMT")[0],
        "10"
      );

      // Check if user had ASAP selected and is changing from current date
      const isCurrentDate = new Date().toDateString() === date.toDateString();
      const wasASAPSelected = selectedTime === "Within 2 hours";

      if (wasASAPSelected && !isCurrentDate) {
        // ...already handled above...
        // You might also need to clear deliverySlotId here if you have access to it
        // handleUpdateBookingObject("deliverySlotId", "", "slot_clear");
      }

      // If date chosen from calendar is outside current visible range,
      // shift the visible start so boxes show the chosen date + next 3 days.
      const start = visibleStartDate ? new Date(visibleStartDate) : new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 3);

      const chosen = new Date(date);
      chosen.setHours(0, 0, 0, 0);
      if (chosen < start || chosen > end) {
        setVisibleStartDate(chosen);
        // Also set calendar month to chosen date's month so reopening shows it
        setCurrentMonth(new Date(chosen.getFullYear(), chosen.getMonth(), 1));
      }

      // Close the calendar after a date is selected from the calendar
      setShowCalendar(false);
    }
  };

  const goToPreviousMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCurrentMonth(prevMonth);
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentMonth(nextMonth);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
    const cells = [];

    // Total number of cells to render for 6 rows x 7 columns
    const TOTAL_CELLS = 42;

    // Fill initial empty cells before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(
        <div key={`empty-start-${i}`} className="h-[35.5px] w-full" />
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      const isDisabled = isDateDisabled(date);
      const isSelected = isDateSelected(date);

      cells.push(
        <button
          key={`day-${day}`}
          className={`flex items-center justify-center h-[35.5px] w-full text-sm rounded ${isSelected
            ? "bg-[#34318C] text-white"
            : isDisabled
              ? "text-gray-300 opacity-50 pointer-events-none"
              : "text-gray-800 hover:bg-gray-100"
            }`}
          onClick={() => {
            handleDateClick(date);
          }}
          disabled={isDisabled}
        >
          <span className="inline-block w-6 text-center">{day}</span>
        </button>
      );
    }

    // Add trailing empty cells to make up 42 total cells
    const remaining = TOTAL_CELLS - cells.length;
    for (let i = 0; i < remaining; i++) {
      cells.push(<div key={`empty-end-${i}`} className="h-[35.5px] w-full" />);
    }

    return cells;
  };

  // Determine whether to show cards or calendar
  const shouldShowCards = !showCalendar;

  return (
    <div className="px-[33px] flex flex-col justify-center items-center text-gray-800 font-instrument max-w-container">
      {/* Header with back button and title */}
      <div className="flex flex-col gap-1 mb-[8px] self-start">
        <div className="text-[#211F59] text-[24px] font-semibold" style={{ lineHeight: "23px" }}>
          When should we arrive?
        </div>

        <div className="text-[12px] text-[#211F59] mb-[19.52px]">
          Choose a pickup date and arrival window.
        </div>
      </div>

      <h3 className="text-[#211F59] font-semibold text-[14px] mb-2 self-start">
        Choose a date
      </h3>

      {/* Date Selection Section */}
      <div className="mb-[21px] w-full border-[#7876A1]">
        {shouldShowCards ? (
          <div className="flex justify-between mb-[]">
            {nextDates.map((date, index) => (
              <button
                style={{ border: "1px solid #7876A1" }}
                key={index}
                type="button"
                className={`flex flex-col text-[#4743BF] font-semibold w-[55px] h-[71px] items-center justify-center gap-[6px]
    rounded-[12px] min-393:w-[61px] min-393:h-[82px] min-393:rounded-[15px]
    ${selectedDate &&
                    selectedDate.toDateString() === date.toDateString()
                    ? "bg-[#34318C] text-white"
                    : "bg-[#F6F6F6]"
                  }`}
                onClick={() => handleDateSelect(date)}
              >
                <span className="min-393:text-[12px] text-[10px] min-393:font-semibold leading-none">
                  {formatDate(date)}
                </span>

                <span className="min-393:text-[34px] text-[28px] min-393:font-semibold leading-none">
                  {formatDay(date)}
                </span>
              </button>

            ))}
            <button
              style={{ border: "1px solid #7876A1" }}
              type="button"
              className="flex items-center font-semibold justify-center min-393:w-[61px] min-393:h-[82px] rounded-[12px] w-[55px] h-[71px] text-[#4743BF] bg-[#F6F6F6] min-393:rounded-[15px] text-sm"
              onClick={() => {
                // Open calendar on the selected date's month if available,
                // otherwise default to current month.
                if (selectedDate) {
                  const d = new Date(selectedDate);
                  setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                } else if (visibleStartDate) {
                  const d = new Date(visibleStartDate);
                  setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                } else {
                  setCurrentMonth(new Date());
                }
                setShowCalendar(true);
              }}
            >
              <span>More Dates</span>
            </button>
          </div>
        ) : (
          <CalendarComponent
            goToPreviousMonth={goToPreviousMonth}
            goToNextMonth={goToNextMonth}
            currentMonth={currentMonth}
            renderCalendar={renderCalendar}
            setShowCalendar={setShowCalendar}
          />
        )}
      </div>

      <h3 className="text-base font-medium mb-2 self-start">
        Choose a time-slot
      </h3>
      {/* Time Selection Section */}
      <div className="mb-4 w-full">
        <div className="grid grid-cols-2 gap-x-[4px] gap-y-[3.5px] w-full">
          {availableTimeSlots.map((time, index) => (
            <button
              style={{ border: "1px solid #7876A1" }}
              key={index}
              type="button"
              className={`px-3 py-2 h-[48px] max-w-[160px] rounded-[15px] text-[14px] font-semibold 
    ${selectedTime === time
                  ? "bg-[#34318C] text-white border-[#34318C]"
                  : "text-[#4743BF] bg-[#F6F6F6]"
                }`}
              onClick={() => handleTimeSelect(time)}
            >
              {formatSlotDisplay(time)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DateAndTimeScreen;
